import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { trackB2BLead, trackSignup } from '@/utils/fbPixel';

// Neutral theme for registration (no UnCloned branding)
// Once validated, client's actual theme will be applied
const DEFAULT_THEME = {
  brand_color: "#3B82F6",           // Neutral blue
  background_color: "#0F172A",      // Slate-900
  card_color: "#1E293B",            // Slate-800
  text_color: "#F8FAFC",            // Slate-50
  // Legacy mappings
  primary_color: "#3B82F6",
  secondary_color: "#3B82F6",       // Same as primary - no gradients
  background_primary: "#0F172A",
  background_secondary: "#1E293B",
  background_tertiary: "#334155",
  text_primary: "#F8FAFC",
  text_secondary: "#CBD5E1",
  border_color: "#334155",
  error_color: "#ef4444",
  mode: "flat"
};

// Helper to merge client theme with defaults for B2B (flat colors only)
const mergeB2BTheme = (clientTheme) => {
  if (!clientTheme) return DEFAULT_THEME;
  
  // Check if client has the new 4-color fields
  const hasNewFields = clientTheme.brand_color || clientTheme.background_color || 
                       clientTheme.card_color || clientTheme.text_color;
  
  if (hasNewFields) {
    const brand = clientTheme.brand_color || DEFAULT_THEME.brand_color;
    const background = clientTheme.background_color || DEFAULT_THEME.background_color;
    const card = clientTheme.card_color || DEFAULT_THEME.card_color;
    const text = clientTheme.text_color || DEFAULT_THEME.text_color;
    
    return {
      brand_color: brand,
      background_color: background,
      card_color: card,
      text_color: text,
      primary_color: brand,
      secondary_color: brand,  // Same as brand - NO gradients
      background_primary: background,
      background_secondary: card,
      background_tertiary: card,
      text_primary: text,
      text_secondary: text,
      border_color: card,
      error_color: "#ef4444",
      mode: "flat"
    };
  }
  
  // Legacy fallback
  return { ...DEFAULT_THEME, ...clientTheme, mode: "flat" };
};

export default function InviteRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    validateInvite();
  }, [token]);

  // Apply CSS variables for B2B theme when theme state changes
  useEffect(() => {
    const root = document.documentElement;
    const primaryColor = theme.brand_color || theme.primary_color;
    const accentColor = theme.accent_color || theme.secondary_color || primaryColor;
    
    // Set CSS variables for global styling (including toasts)
    root.style.setProperty('--theme-primary', primaryColor);
    root.style.setProperty('--theme-accent', accentColor);
    root.style.setProperty('--theme-card', theme.card_color || theme.background_secondary);
    root.style.setProperty('--theme-text', theme.text_color || theme.text_primary);
    root.style.setProperty('--theme-border', theme.border_color);
    
    // Calculate button text color based on primary color luminance
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    root.style.setProperty('--theme-button-text', luminance > 0.5 ? '#1a1a2e' : '#ffffff');
    
    // Cleanup on unmount - reset to defaults
    return () => {
      root.style.removeProperty('--theme-primary');
      root.style.removeProperty('--theme-accent');
      root.style.removeProperty('--theme-card');
      root.style.removeProperty('--theme-text');
      root.style.removeProperty('--theme-border');
      root.style.removeProperty('--theme-button-text');
    };
  }, [theme]);

  const validateInvite = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/auth/invite/${token}`);
      setInviteData(res.data);
      setFormData(prev => ({
        ...prev,
        firstName: res.data.first_name || '',
        lastName: res.data.last_name || ''
      }));
      
      // Apply client theme if available - use flat color merge
      if (res.data.client?.theme) {
        setTheme(mergeB2BTheme(res.data.client.theme));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      // SECURITY: Auth token set via httpOnly cookie by backend
      const res = await axios.post(`${API}/auth/register-invited`, {
        invite_token: token,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName
      });
      
      // Track Facebook Pixel - B2B signup
      trackSignup({ method: 'email' });
      trackB2BLead({ client_name: inviteData?.client?.name });
      
      // Check if existing user was linked
      if (res.data.existing_user) {
        toast.success('Your account has been linked to this program!');
      } else {
        toast.success('Account created successfully!');
      }
      
      // Redirect to B2B portal login so they know where to log in next time
      const clientId = inviteData?.client?.client_id;
      if (clientId) {
        navigate(`/portal/${clientId}/login`, { 
          state: { justRegistered: true, email: inviteData?.email }
        });
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const client = inviteData?.client;
  const appName = client?.app_name || 'Journal';

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: theme.background_color || theme.background_primary }}
      >
        <div 
          className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: theme.brand_color || theme.primary_color, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: theme.background_color || theme.background_primary }}
      >
        <div 
          className="rounded-xl p-8 w-full max-w-md text-center"
          style={{
            backgroundColor: theme.card_color || theme.background_secondary,
            border: `1px solid ${theme.border_color}`
          }}
        >
          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: theme.error_color }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: theme.text_color || theme.text_primary }}>Invalid Invitation</h1>
          <p className="mb-6" style={{ color: theme.text_color || theme.text_secondary }}>{error}</p>
          <Button 
            onClick={() => navigate('/login')}
            style={{
              backgroundColor: theme.brand_color || theme.primary_color,
              color: theme.text_color || theme.text_primary
            }}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: theme.background_color || theme.background_primary }}
    >
      <div 
        className="rounded-xl p-8 w-full max-w-md"
        style={{
          backgroundColor: theme.card_color || theme.background_secondary,
          border: `1px solid ${theme.border_color}`
        }}
      >
        {/* Client Branding */}
        <div className="text-center mb-8">
          {client?.logo_url ? (
            <img src={client.logo_url} alt={client.name} className="h-16 mx-auto mb-4" />
          ) : (
            <div 
              className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white font-bold text-3xl"
              style={{ backgroundColor: theme.brand_color || theme.primary_color }}
            >
              {client?.name?.charAt(0) || appName.charAt(0)}
            </div>
          )}
          <h1 className="text-2xl font-bold mb-2" style={{ color: theme.text_color || theme.text_primary }}>
            {client?.name || appName}
          </h1>
          <p className="text-sm" style={{ color: theme.text_color || theme.text_secondary }}>
            Create your account to join {client?.name || 'our'}'s journaling program
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email (read-only) */}
          <div>
            <label className="block text-sm mb-1" style={{ color: theme.text_color || theme.text_secondary }}>Email</label>
            <Input
              type="email"
              value={inviteData?.email || ''}
              disabled
              className="opacity-70"
              style={{
                backgroundColor: theme.background_color || theme.background_primary,
                borderColor: theme.border_color,
                color: theme.text_color || theme.text_primary
              }}
            />
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1" style={{ color: theme.text_color || theme.text_secondary }}>First Name</label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                placeholder="John"
                style={{
                  backgroundColor: theme.background_color || theme.background_primary,
                  borderColor: theme.border_color,
                  color: theme.text_color || theme.text_primary
                }}
              />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: theme.text_color || theme.text_secondary }}>Last Name</label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                placeholder="Doe"
                style={{
                  backgroundColor: theme.background_color || theme.background_primary,
                  borderColor: theme.border_color,
                  color: theme.text_color || theme.text_primary
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm mb-1" style={{ color: theme.text_color || theme.text_secondary }}>Password</label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Create a password (min 8 characters)"
                className="pr-10"
                style={{
                  backgroundColor: theme.background_color || theme.background_primary,
                  borderColor: theme.border_color,
                  color: theme.text_color || theme.text_primary
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-100"
                style={{ color: theme.text_color || theme.text_secondary }}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm mb-1" style={{ color: theme.text_color || theme.text_secondary }}>Confirm Password</label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              placeholder="Confirm your password"
              style={{
                backgroundColor: theme.background_color || theme.background_primary,
                borderColor: theme.border_color,
                color: theme.text_color || theme.text_primary
              }}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-xs mt-1" style={{ color: theme.error_color }}>Passwords do not match</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting || !formData.password || formData.password !== formData.confirmPassword}
            className="w-full py-3 text-lg font-semibold"
            style={{ 
              backgroundColor: theme.brand_color || theme.primary_color,
              color: theme.text_color || theme.text_primary
            }}
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Create My Account'
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: theme.text_color || theme.text_secondary }}>
          Powered by {client?.name || appName}
        </p>
      </div>
    </div>
  );
}
